import * as React from 'react';
import { ErrorNotification, SuccessNotification, WarningNotification } from '@verclam/toast-v2';

/**
 * `eventName` matches the `type` of the actions dispatched by the gabarit application layer
 * (`Application/Documents/Templates`, `Application/Documents/TemplateAssignments`), by the document
 * type application layer (`Application/Documents/DocumentTypes` — create, rename, archive, restore),
 * and by `describeDocumentTemplateFile` (`Application/Documents/TemplateFiles`). Mounted by both
 * gabarit screens (organization and facility): only one is ever mounted at a time, same reasoning as
 * `AiAssignmentsNotifications` — the facility screen never manages document types, but still gets the
 * gabarit and assignment toasts from the same mount. Mutation failures and the file-reading failure
 * show a toast, as does the section-dependency warning of a deposit — list reading failures never do, the screen's own `ErrorState` already covers them.
 */
const DocumentTemplatesNotifications: React.FC = () => {
	return (
		<React.Fragment>
			<SuccessNotification
				eventName="DOCUMENT_TEMPLATE_CREATED"
				title="Gabarit déposé"
				message="Le gabarit a bien été déposé."
			/>
			<SuccessNotification
				eventName="DOCUMENT_TEMPLATE_VERSION_UPLOADED"
				title="Nouvelle version déposée"
				message="La nouvelle version a bien été déposée."
			/>
			<SuccessNotification
				eventName="DOCUMENT_TEMPLATE_VERSION_SECTIONS_UPDATED"
				title="Sections enregistrées"
				message="Sections de réévaluation enregistrées."
			/>
			<SuccessNotification
				eventName="DOCUMENT_TEMPLATE_UPDATED"
				title="Gabarit modifié"
				message="Le gabarit a bien été modifié."
			/>
			<SuccessNotification
				eventName="DOCUMENT_TEMPLATE_ARCHIVED"
				title="Gabarit archivé"
				message="Le gabarit a bien été archivé."
			/>
			<SuccessNotification
				eventName="TEMPLATE_ASSIGNMENT_SAVED"
				title="Affectation enregistrée"
				message="L’affectation a bien été enregistrée."
			/>
			<SuccessNotification
				eventName="TEMPLATE_ASSIGNMENT_CLEARED"
				title="Affectation retirée"
				message="L’affectation a bien été retirée."
			/>
			<SuccessNotification
				eventName="FACILITY_TEMPLATE_OVERRIDE_REMOVED"
				title="Surcharge retirée"
				message="La surcharge a bien été retirée."
			/>
			<WarningNotification
				eventName="DOCUMENT_TEMPLATE_SECTION_DEPENDENCIES_WARNED"
				title="Dépendances entre sections"
				message="Certaines dépendances entre sections ne s'appliquent pas à ce gabarit."
			/>
			<ErrorNotification
				eventName="DOCUMENT_TEMPLATE_CREATION_FAILED"
				title="Erreur"
				message="Le gabarit n’a pas pu être déposé."
			/>
			<ErrorNotification
				eventName="DOCUMENT_TEMPLATE_VERSION_UPLOAD_FAILED"
				title="Erreur"
				message="La nouvelle version n’a pas pu être déposée."
			/>
			<ErrorNotification
				eventName="DOCUMENT_TEMPLATE_VERSION_SECTIONS_UPDATE_FAILED"
				title="Erreur"
				message="Les sections de réévaluation n’ont pas pu être enregistrées."
			/>
			<ErrorNotification
				eventName="DOCUMENT_TEMPLATE_UPDATE_FAILED"
				title="Erreur"
				message="Le gabarit n’a pas pu être modifié."
			/>
			<ErrorNotification
				eventName="DOCUMENT_TEMPLATE_ARCHIVE_FAILED"
				title="Erreur"
				message="Le gabarit n’a pas pu être archivé."
			/>
			<ErrorNotification
				eventName="TEMPLATE_ASSIGNMENT_SAVE_FAILED"
				title="Erreur"
				message="L’affectation n’a pas pu être enregistrée."
			/>
			<ErrorNotification
				eventName="TEMPLATE_ASSIGNMENT_CLEAR_FAILED"
				title="Erreur"
				message="L’affectation n’a pas pu être retirée."
			/>
			<ErrorNotification
				eventName="FACILITY_TEMPLATE_OVERRIDE_REMOVE_FAILED"
				title="Erreur"
				message="La surcharge n’a pas pu être retirée."
			/>
			<ErrorNotification
				eventName="DOCUMENT_TEMPLATE_FILE_DESCRIPTION_FAILED"
				title="Erreur"
				message="Le fichier n’a pas pu être lu."
			/>
			<SuccessNotification
				eventName="DOCUMENT_TYPE_CREATED"
				title="Type de document ajouté"
				message="Le type de document a bien été ajouté."
			/>
			<SuccessNotification
				eventName="DOCUMENT_TYPE_RENAMED"
				title="Type de document renommé"
				message="Le type de document a bien été renommé."
			/>
			<SuccessNotification
				eventName="DOCUMENT_TYPE_ARCHIVED"
				title="Type de document archivé"
				message="Le type de document a bien été archivé."
			/>
			<SuccessNotification
				eventName="DOCUMENT_TYPE_RESTORED"
				title="Type de document désarchivé"
				message="Le type de document a bien été désarchivé."
			/>
			<ErrorNotification
				eventName="DOCUMENT_TYPE_CREATION_FAILED"
				title="Erreur"
				message="Le type de document n’a pas pu être ajouté."
			/>
			<ErrorNotification
				eventName="DOCUMENT_TYPE_RENAMING_FAILED"
				title="Erreur"
				message="Le type de document n’a pas pu être renommé."
			/>
			<ErrorNotification
				eventName="DOCUMENT_TYPE_ARCHIVING_FAILED"
				title="Erreur"
				message="Le type de document n’a pas pu être archivé."
			/>
			<ErrorNotification
				eventName="DOCUMENT_TYPE_RESTORING_FAILED"
				title="Erreur"
				message="Le type de document n’a pas pu être désarchivé."
			/>
		</React.Fragment>
	);
};

export default DocumentTemplatesNotifications;
